/**
 * Accessible reordering via a drag handle (SPEC §6.4):
 * - keyboard: Space/Enter lifts, arrow keys move, Space/Enter drops, Escape cancels;
 * - pointer: press and drag the handle over another item, release to drop.
 * Moves are applied live inside a store gesture, so a whole drag is one undo
 * step and cancelling restores the original order.
 */
import { tick } from 'svelte';

export interface ReorderMessages {
  lifted: (label: string, position: number, count: number) => string;
  moved: (label: string, position: number, count: number) => string;
  dropped: (label: string, position: number, count: number) => string;
  cancelled: (label: string) => string;
}

export interface ReorderTarget {
  /** Groups items that can swap places; matches `data-reorder-group` on the items. */
  group: string;
  id: string;
  label: string;
  index: () => number;
  count: () => number;
  move: (to: number) => void;
  begin: () => void;
  end: (commit: boolean) => void;
  announce: (message: string) => void;
  messages: ReorderMessages;
}

/** Which item (if any) is currently being moved, shared by a list's handles. */
export class ReorderState {
  active: string | null = $state(null);
}

export interface HandleProps {
  'aria-pressed': boolean;
  'data-reorder-handle': string;
  onkeydown: (event: KeyboardEvent) => void;
  onpointerdown: (event: PointerEvent) => void;
  onblur: () => void;
  style: string;
}

const EDGE = 64;

export function reorderHandle(state: ReorderState, target: ReorderTarget): HandleProps {
  let refocusing = false;
  let pointerCleanup: (() => void) | null = null;

  const position = () => target.index() + 1;
  const isActive = () => state.active === target.id;

  function lift() {
    target.begin();
    state.active = target.id;
    target.announce(target.messages.lifted(target.label, position(), target.count()));
  }

  function finish(commit: boolean) {
    if (!isActive()) return;
    pointerCleanup?.();
    state.active = null;
    target.end(commit);
    target.announce(
      commit
        ? target.messages.dropped(target.label, position(), target.count())
        : target.messages.cancelled(target.label),
    );
  }

  async function moveTo(to: number, handle: HTMLElement | null) {
    const clamped = Math.min(Math.max(to, 0), target.count() - 1);
    if (clamped === target.index()) return;
    refocusing = true;
    target.move(clamped);
    target.announce(target.messages.moved(target.label, clamped + 1, target.count()));
    await tick();
    // Keyed lists move DOM nodes, which drops focus; put it back.
    (handle?.isConnected ? handle : findHandle(target.id))?.focus();
    refocusing = false;
  }

  function findHandle(id: string): HTMLElement | null {
    return document.querySelector<HTMLElement>(`[data-reorder-handle="${CSS.escape(id)}"]`);
  }

  function onkeydown(event: KeyboardEvent) {
    const handle = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    const key = event.key;
    if (!isActive()) {
      if (key === ' ' || key === 'Enter') {
        event.preventDefault();
        lift();
      }
      return;
    }
    const moves: Record<string, number> = {
      ArrowUp: target.index() - 1,
      ArrowLeft: target.index() - 1,
      ArrowDown: target.index() + 1,
      ArrowRight: target.index() + 1,
      Home: 0,
      End: target.count() - 1,
    };
    const to = moves[key];
    if (to !== undefined) {
      event.preventDefault();
      void moveTo(to, handle);
    } else if (key === ' ' || key === 'Enter') {
      event.preventDefault();
      finish(true);
    } else if (key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      finish(false);
    }
  }

  function onpointerdown(event: PointerEvent) {
    if (event.button !== 0 || isActive()) return;
    const handle = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    if (!handle) return;
    event.preventDefault();
    lift();
    // Listen on the window: moving the dragged node in the DOM releases pointer capture.
    const pointerId = event.pointerId;

    const onmove = (move: PointerEvent) => {
      if (move.pointerId !== pointerId) return;
      if (move.clientY < EDGE) window.scrollBy(0, -12);
      else if (move.clientY > window.innerHeight - EDGE) window.scrollBy(0, 12);
      const items = [
        ...document.querySelectorAll<HTMLElement>(
          `[data-reorder-group="${CSS.escape(target.group)}"]`,
        ),
      ];
      const over = items.find((item) => {
        const rect = item.getBoundingClientRect();
        return (
          move.clientX >= rect.left &&
          move.clientX <= rect.right &&
          move.clientY >= rect.top &&
          move.clientY <= rect.bottom
        );
      });
      const overIndex = over ? items.indexOf(over) : -1;
      if (over && over.dataset.reorderId !== target.id && overIndex !== -1) {
        void moveTo(overIndex, handle);
      }
    };
    const onup = () => {
      finish(true);
    };
    const oncancel = () => {
      finish(false);
    };
    const onkey = (key: KeyboardEvent) => {
      if (key.key === 'Escape') finish(false);
    };
    window.addEventListener('pointermove', onmove);
    window.addEventListener('pointerup', onup);
    window.addEventListener('pointercancel', oncancel);
    window.addEventListener('keydown', onkey);
    pointerCleanup = () => {
      window.removeEventListener('pointermove', onmove);
      window.removeEventListener('pointerup', onup);
      window.removeEventListener('pointercancel', oncancel);
      window.removeEventListener('keydown', onkey);
      pointerCleanup = null;
    };
  }

  return {
    get 'aria-pressed'() {
      return isActive();
    },
    'data-reorder-handle': target.id,
    onkeydown,
    onpointerdown,
    onblur: () => {
      if (!refocusing && pointerCleanup === null) finish(true);
    },
    style: 'touch-action: none',
  };
}
